/**
 * ViewSwitcher Component - Optimized view switching without rerenders
 * This component handles the view logic and prevents unnecessary rerenders
 */

import { type FC, memo, useLayoutEffect, useRef } from 'react';
import { useSections } from 'store/app/appStore';
import { playViewSwitchEffect } from 'shared/effects';
import MultiViewLobby from '../../../../layouts/Lobby/MultiViewLobby';
import SingleViewLobby from '../../../../layouts/Lobby/SingleViewLobby';

interface ViewSwitcherProps {
  showTournamentNavigation?: boolean;
  campaingOnlyView?: boolean;
}

const ViewSwitcher: FC<ViewSwitcherProps> = ({ showTournamentNavigation, campaingOnlyView }) => {
  const { viewMode } = useSections();

  // POC animated lobby (?fx=1): crossfade + card stagger on the container that just became visible.
  // Layout effect so the class is on before the first paint of the switched view; skips the mount.
  const previousViewMode = useRef(viewMode);
  useLayoutEffect(() => {
    if (previousViewMode.current === viewMode) return undefined;
    previousViewMode.current = viewMode;
    return playViewSwitchEffect(viewMode);
  }, [viewMode]);

  // Keep both views mounted; toggle visibility with lightweight wrappers.
  return (
    <>
      <MultiViewLobby hidden={viewMode === 'single'} />
      <SingleViewLobby hidden={viewMode === 'multi'} showTournamentNavigation={showTournamentNavigation} campaingOnlyView={campaingOnlyView}/>
    </>
  );
};

// Memoize aggressively - this component should almost never rerender
export default memo(ViewSwitcher, (prevProps, nextProps) => {
  return prevProps.showTournamentNavigation === nextProps.showTournamentNavigation &&
    prevProps.campaingOnlyView === nextProps.campaingOnlyView
});
